import moment from "moment";
import React, { useEffect, useState } from "react";
import { DropdownList } from "react-widgets";
import api from "../../../api";
import { DateInput } from "../../../components/form-group";
import { combineDateAndTime } from "../../../utils/CombineDateAndTime";
import { useAppSettings } from "../../hooks/useAppSettings";
import {
  DateFormats,
  getDayDifference,
  getMinuteDifference,
  getMinutes,
  singleDayTimeBuilder,
} from "../../utils";
import { ITimePeriodData } from "./custom-time-period";

interface ICustomTimeInputProps {
  defaultValue?: ITimePeriodData;
  onChange?: (value: ITimePeriodData) => void;
  timeGap?: number;
  gap?: number;
  minDate?: Date;
  disabled?: boolean;
  appointmentTime?: {
    start: string;
    end: string;
  };
  hideEndTime?: boolean;
  isEndTime?: boolean;
  excludeDates?: Date[];
  useMinimumDuration?: boolean;
  overnightStartDate?: ITimePeriodData;
  range?: string
}

export const SingleDayTimePeriod: React.FunctionComponent<ICustomTimeInputProps> = (
  props
) => {
  const [endData, setEndData] = useState<{ label: string; value: string }[]>(
    []
  );

  const [adjustedEndData, setAdjustedEndData] = useState<
    { label: string; value: string }[]
  >([]);
  const appSettings = useAppSettings(api.settings);

  const [startData, setStartData] = useState<
    { label: string; value: string }[]
  >([]);
  const startOfToday = new Date();
  const defaultStartDate = props.isEndTime
    ? props.defaultValue?.endDate
    : props.defaultValue?.startDate;
  const [startDate, setStartDate] = useState<Date>(
    defaultStartDate ?? startOfToday
  );
  const [startTime, setStartTime] = useState(
    props.defaultValue?.startTime ?? ""
  );
  const [endTime, setEndTime] = useState(props.defaultValue?.endTime ?? "");
  const [endDate, setEndDate] = useState<Date>(
    props.defaultValue?.endDate ?? startOfToday
  );
  React.useEffect(() => {
    const settingsStartDate = combineDateAndTime(
      moment(startDate).startOf("day").toDate(),
      props?.appointmentTime?.start || "12:00 AM"
    );

    const settingsEndDate = combineDateAndTime(
      moment(startDate).startOf("day").toDate(),
      props?.appointmentTime?.end || "11:55 PM"
    );

    setStartData(
      singleDayTimeBuilder(undefined, props.gap)?.filter(
        (x) =>
          combineDateAndTime(
            moment(startDate).startOf("day").toDate(),
            x.value
          ) >= settingsStartDate &&
          combineDateAndTime(
            moment(startDate).startOf("day").toDate(),
            x.value
          ) <= settingsEndDate
      )
    );
    setEndDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    setEndDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime]);

  useEffect(() => {
    handleOnChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime]);

  const handleOnChange = () => {
    const date = startDate;
    const value: ITimePeriodData = {
      startDate: date,
      startTime: startTime ? startTime : undefined,
      endDate: date,
      endTime: endTime ? endTime : undefined,
    };
    if (props.onChange) {
      props.onChange(value);
    }
  };

  const setEndDropdownData = () => {
    if (startDate != null && endDate != null && startTime !== "") {
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf("day").toDate(),
        startTime
      );

      const settingsStartDate = combineDateAndTime(
        moment(startDate).startOf("day").toDate(),
        props?.appointmentTime?.start || "12:00 AM"
      );

      const settingsEndDate = combineDateAndTime(
        moment(startDate).startOf("day").toDate(),
        props?.appointmentTime?.end || "11:55 PM"
      );
      const initialEndData = singleDayTimeBuilder(
        startDateTime,
        props.gap
      )?.filter(
        (x) =>
          combineDateAndTime(
            moment(startDate).startOf("day").toDate(),
            x.value
          ) >= settingsStartDate &&
          combineDateAndTime(
            moment(startDate).startOf("day").toDate(),
            x.value
          ) <= settingsEndDate
      );

      const minimumDurationInMinutes = getMinutes(
        appSettings?.data?.activity?.activityMinimumDuration || {
          value: 15,
          unit: "Minutes",
        }
      );
      const index =
        props.useMinimumDuration && minimumDurationInMinutes > (props.gap || 5)
          ? Math.trunc(minimumDurationInMinutes / (props.gap || 5)) - 1
          : 0;
      if (index > 0) {
        const newEndData = initialEndData.slice(index);
        setEndData(newEndData);
        if (
          props.overnightStartDate?.startDate?.getDate() === endDate?.getDate()
        ) {
          setAdjustedEndData(newEndData);
        } else {
          setAdjustedEndData(startData);
        }
      } else {
        setEndData(initialEndData);
        setAdjustedEndData(startData);
      }
    }
  };

  React.useEffect(() => {
    if (endDate != null && endTime !== "" && endData.length > 0) {
      const isValidObject = endData.find((p) => p.value === endTime) != null;
      if (!isValidObject) {
        setEndTime(endData[0].value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endData]);

  const handleStartDateChange = (date: Date) => {
    date = combineDateAndTime(date, startTime);
    const dayGap = getDayDifference(startDate, endDate);
    setStartDate(date);
    setEndDate(moment(date).add(dayGap, "days").toDate());
  };

  const handleStartTimeChange = (time: string) => {
    if (
      endTime !== "" &&
      endDate !== undefined &&
      startTime !== "" &&
      startDate !== undefined
    ) {
      const endDateTime = combineDateAndTime(
        moment(endDate).startOf("day").toDate(),
        endTime
      );
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf("day").toDate(),
        startTime
      );
      const minuteGap = getMinuteDifference(startDateTime, endDateTime);

      const newStartDateTime = combineDateAndTime(startDate, time);
      const newEndDateTime = moment(newStartDateTime).add(minuteGap, "minutes");
      const newEndDate = newEndDateTime.toDate();

      if (moment(startDate).diff(newEndDate, "day") > 1) {
        setEndTime("11:55 PM");
        setEndDate(startDate);
      } else {
        setEndTime(newEndDateTime.format(DateFormats.TimeOnlyFormat));
        setEndDate(newEndDate);
      }
    }
    setStartTime(time);
  };

  return (
    <div className="custom-time-input">
      <div className="row gutter-8">
        <div className="col-lg-4 col-12 mb-lg-0 mb-3 time-period">
          <DateInput
            default={startDate}
            placeholder={"dd/mm/yyyy"}
            minDate={props.minDate}
            onChange={(date) => {
              handleStartDateChange(date);
            }}
            useDefult={true}
            disabled={props.disabled}
            excludeDates={props.excludeDates || []}
          />
        </div>
        <div className="col-lg-3 mb-lg-0 mb-3">
          <div className="position-relative time-period">
            <DropdownList
              placeholder={"HH:mm"}
              containerClassName="time-period-container"
              textField="label"
              valueField="value"
              //  placeholder={props.placeHolder}
              value={props.isEndTime ? endTime : startTime}
              onChange={(val) => {
                if (!props.isEndTime) {
                  handleStartTimeChange(val.value);
                } else {
                  setEndTime(val.value);
                }
              }}
              data={props.isEndTime ? adjustedEndData : startData}
              disabled={props.disabled}
              itemComponent={({item}) => (
                <div className={`m-2 cursor-pointer ${
                  (props.isEndTime ? endTime : startTime) === item.value 
                    ? 'active' 
                    : ''
                }`}>
                  {item.label}
                </div>
              )}
            />
            {startTime !== "" && !props.disabled && (
              <i
                onClick={() => {
                  setStartTime("");
                }}
                className="icon-close position-absolute bg-white align-y-center font-xxxs text-center p-2 font-weight-bold cursor-pointer time-close"
              ></i>
            )}
          </div>
        </div>
        {!props.hideEndTime && (
          <div className="col-lg-5 mb-sm-0 mb-3">
            <div className="position-relative time-period">
              <DropdownList
                placeholder={"HH:mm"}
                containerClassName="time-period-container"
                textField="label"
                valueField="value"
                //  placeholder={props.placeHolder}
                value={endTime}
                onChange={(val) => {
                  setEndTime(val.value);
                }}
                data={endData}
                disabled={props.disabled}
                itemComponent={({item}) => (
                  <div className={`m-2 cursor-pointer ${
                    endTime === item.value 
                      ? 'active' 
                      : ''
                  }`}>
                    {item.label}
                  </div>
                )}
              />
              {endTime !== "" && !props.disabled && (
                <i
                  onClick={() => {
                    setEndTime("");
                  }}
                  className="icon-close position-absolute bg-white align-y-center font-xxxs text-center p-2 font-weight-bold cursor-pointer time-close"
                ></i>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const isTimePeriodValid = (period: ITimePeriodData) => {
  return period?.startDate && period?.startTime && period.endTime;
};
